import { z } from 'zod';
import { AvailabilityPeriodCellSchema } from './availability.js';
import { OfferSnapshotUnitSchema } from './offer-version.js';
import { OfferVersionSummaryForClientSchema, ClientDiffItemSchema } from './offer-version-diff.js';
import {
  CarryForwardPrefillSchema,
  RecordedUnitDecisionsSchema,
} from './offer-version-unit-decisions.js';

// ─── Public offer view unit ────────────────────────────────────────────────────

/**
 * A single unit in the public client view response.
 * Extends the snapshot unit with optional live-enriched display fields
 * (coordinates from the live media_units record, signed photo URL).
 */
export const PublicOfferViewUnitSchema = OfferSnapshotUnitSchema.extend({
  longitude: z.number().nullable(),
  latitude: z.number().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  // Server-resolved rendition URLs (media-photo-derivatives), replacing photoUrl.
  // Null when the live media unit/photo is absent — see photoUrl's prior contract.
  thumbnailUrl: z.string().nullable(),
  displayUrl: z.string().nullable(),
  /** Client-visible free-text location description, live-resolved from media_units.location_description. */
  locationDescription: z.string().nullable(),
  availability: z.array(AvailabilityPeriodCellSchema),
});
export type PublicOfferViewUnit = z.infer<typeof PublicOfferViewUnitSchema>;

// ─── Public client view response ───────────────────────────────────────────────

/**
 * Version summary shape for the public client view.
 * Extends OfferVersionSummaryForClientSchema with snapshot-root metadata fields
 * (clientName, offerTitle) so that the page does not need to cast per-unit mediaUnitDetails.
 */
export const PublicOfferVersionSummarySchema = OfferVersionSummaryForClientSchema.extend({
  clientName: z.string().optional(),
  offerTitle: z.string().optional(),
});
export type PublicOfferVersionSummary = z.infer<typeof PublicOfferVersionSummarySchema>;

/**
 * Response from GET /public/offers/:token/client-view.
 * Separate from ClientOfferViewResponseSchema — never modifies the authenticated shape.
 * units: always present (may be empty); carries snapshot terms + optional display enrichment.
 * diff: null on first view; ClientDiffItem[] when newer version exists.
 */
export const PublicOfferViewResponseSchema = z.object({
  version: PublicOfferVersionSummarySchema,
  diff: z.array(ClientDiffItemSchema).nullable(),
  units: z.array(PublicOfferViewUnitSchema),
  validUntil: z.coerce.date(),
  /**
   * Offer-level campaign range, used by the client to fall back a never-reserved
   * unit's displayed period instead of "today–today" (CU-86caypx6r).
   */
  defaultDateFrom: z.coerce.date().nullable(),
  defaultDateTo: z.coerce.date().nullable(),
  /**
   * Top-level sibling of `version` (NOT nested under it) — the client's
   * carried-over per-unit accept/period pre-fill (offer-decision-carry-forward).
   * Required, non-optional: an empty pre-fill is `[]`, never omitted.
   */
  carryForwardPrefill: CarryForwardPrefillSchema,
  /**
   * Top-level sibling of `version` (NOT nested under it) — the per-unit
   * decisions recorded when this version was accepted
   * (client-offer-decision-persistence). Optional, unlike carryForwardPrefill:
   * an older api deployment omits this key entirely, and that MUST still
   * parse so a rollback does not break the whole client offer page. Absent
   * for a version that is not accepted; an empty array for an accepted
   * version with no recorded decisions.
   */
  recordedDecisions: RecordedUnitDecisionsSchema.optional(),
});
export type PublicOfferViewResponse = z.infer<typeof PublicOfferViewResponseSchema>;

// ─── Create public link response ───────────────────────────────────────────────

/**
 * Response from POST /offers/:id/public-link.
 * Returns the plaintext token (shown once) and a relative path.
 * The web client composes the absolute URL from its own origin.
 * No absolute URL field — host is never returned by the API.
 *
 * linkId: the new `offer_public_links` row id — lets the web client pair the plaintext
 * path with its linkId synchronously at generate time, instead of waiting on a status-query
 * refetch to confirm it (CU-86cbc4u7r). Not a secret, see PublicLinkStatusSchema's linkId.
 */
export const CreatePublicLinkResponseSchema = z
  .object({
    token: z.string(),
    path: z.string(),
    expiresAt: z.coerce.date(),
    linkId: z.number(),
  })
  .strict();
export type CreatePublicLinkResponse = z.infer<typeof CreatePublicLinkResponseSchema>;

// ─── Public link status ────────────────────────────────────────────────────────

/**
 * Response from GET /offers/:id/public-link.
 * Reports link state WITHOUT ever returning the plaintext token.
 *
 * firstViewedAt: the durable read-receipt timestamp — when the client first opened
 * the link. Null when the link has never been opened or no link exists. Distinct
 * from any last-viewed timestamp, which is not exposed by this endpoint.
 *
 * linkId: the internal `offer_public_links` row id (null when no active link exists).
 * Not a secret — just lets the web client verify a locally-cached plaintext path still
 * corresponds to the currently-active link row before treating it as copyable
 * (CU-86cbc4u7r).
 */
export const PublicLinkStatusSchema = z
  .object({
    active: z.boolean(),
    expiresAt: z.coerce.date().nullable(),
    revokedAt: z.coerce.date().nullable(),
    firstViewedAt: z.coerce.date().nullable(),
    linkId: z.number().nullable(),
  })
  .strict();
export type PublicLinkStatus = z.infer<typeof PublicLinkStatusSchema>;
